import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectIn3dView } from '../../../map/state/map.selectors';
import { combineLatest, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { MapService } from '@tailormap-viewer/map';
import { FeatureStylingHelper } from '../../../shared/helpers/feature-styling.helper';
import {
  hasSelectedLayersAndGeometry, selectFilterFeatures, selectReferenceLayer,
  selectSelectedFilterGroupError,
  selectSelectedFilterGroupId,
  selectSelectedLayersCount, selectSelectedSpatialFilterFeatureId, selectSpatialFilterHasExceededMaxFeatures,
} from '../state/filter-component.selectors';
import { closeForm } from '../state/filter-component.actions';
import { FeatureModel, FeatureModelAttributes } from '@tailormap-viewer/api';
import { RemoveFilterService } from '../services/remove-filter.service';
import { SpatialFilterReferenceLayerService } from '../../../filter/services/spatial-filter-reference-layer.service';
import { filter } from 'rxjs/operators';
import { TypesHelper, ErrorMessageComponent, TooltipDirective } from '@tailormap-viewer/shared';
import { ApplicationStyleService } from '../../../services/application-style.service';
import { ReferenceLayerService } from '../services/reference-layer.service';
import { SpatialFilterFormSelectLayersComponent } from '../spatial-filter-form-select-layers/spatial-filter-form-select-layers.component';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { SpatialFilterFormDrawGeometriesComponent } from '../spatial-filter-form-draw-geometries/spatial-filter-form-draw-geometries.component';
import { SpatialFilterFormSelectReferenceLayerComponent } from '../spatial-filter-form-select-reference-layer/spatial-filter-form-select-reference-layer.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SpatialFilterFormBufferComponent } from '../spatial-filter-form-buffer/spatial-filter-form-buffer.component';
import { MatButton } from '@angular/material/button';
import { DataSourceManagerService } from '../../../services';

@Component({
    selector: 'tm-spatial-filter-form',
    templateUrl: './spatial-filter-form.component.html',
    styleUrls: ['./spatial-filter-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpatialFilterFormSelectLayersComponent,
        NgTemplateOutlet,
        SpatialFilterFormDrawGeometriesComponent,
        SpatialFilterFormSelectReferenceLayerComponent,
        MatProgressSpinner,
        ErrorMessageComponent,
        SpatialFilterFormBufferComponent,
        MatButton,
        TooltipDirective,
        AsyncPipe,
    ],
})
export class SpatialFilterFormComponent implements OnInit, OnDestroy {
  private store$ = inject(Store);
  private mapService = inject(MapService);
  private removeFilterService = inject(RemoveFilterService);
  private spatialFilterReferenceLayerService = inject(SpatialFilterReferenceLayerService);
  private dataSourceManagerService = inject(DataSourceManagerService);
  private referenceLayerService = inject(ReferenceLayerService);


  private DEFAULT_STYLE = (feature: FeatureModel) => FeatureStylingHelper.getDefaultHighlightStyle('filter-drawing-style', {
    pointType: undefined,
    fillColor: ApplicationStyleService.getPrimaryColor(),
    fillOpacity: 30,
    strokeWidth: 2,
    buffer: feature.attributes?.buffer,
  });

  private destroyed = new Subject();

  public drawingLayerId = 'filter-drawing-layer';
  public availableLayers$ = this.dataSourceManagerService.filterableLayers$;
  public referencableLayers$ = this.referenceLayerService.referencableLayers$;

  public currentGroup$: Observable<string | undefined> = of(undefined);
  public selectedLayersCount$: Observable<number> = of(0);
  public hasSelectedLayersAndGeometry$: Observable<boolean> = of(false);
  public isLoadingReferenceGeometry$: Observable<boolean> = of(false);
  public currentGroupError$: Observable<string | undefined> = of(undefined);
  public currentGroupExceededMaxFeatures$ = this.store$.select(selectSpatialFilterHasExceededMaxFeatures);
  public exceededMaxErrorMessage$ = this.currentGroupExceededMaxFeatures$.pipe(
    filter(hasExceeded => !!hasExceeded),
    switchMap(() => this.getLabelForReferenceLayer$()),
    map((referenceLayerTitle) =>  {
      if (!referenceLayerTitle) {
        return '';
      }
      const maxFeatures = SpatialFilterReferenceLayerService.MAX_REFERENCE_FEATURES;
      // eslint-disable-next-line max-len
      return $localize `:@@core.filter.max-features-exceeded-warning:There are more than ${maxFeatures} objects available in the ${referenceLayerTitle} layer. The maximum is ${maxFeatures}. Apply filters to narrow down the selection.`;
    }),
  );
  public in3dView$: Observable<boolean> = of(false);

  public ngOnInit(): void {
    this.selectedLayersCount$ = this.store$.select(selectSelectedLayersCount);
    this.hasSelectedLayersAndGeometry$ = this.store$.select(hasSelectedLayersAndGeometry);
    this.currentGroup$ = this.store$.select(selectSelectedFilterGroupId);
    this.currentGroupError$ = this.store$.select(selectSelectedFilterGroupError);
    this.in3dView$ = this.store$.select(selectIn3dView);
    this.isLoadingReferenceGeometry$ = this.currentGroup$
      .pipe(
        filter(TypesHelper.isDefined),
        switchMap(groupId => this.spatialFilterReferenceLayerService.isLoadingGeometryForGroup$(groupId)),
      );

    this.mapService.renderFeatures$<FeatureModelAttributes>(
      this.drawingLayerId,
      combineLatest([
        this.store$.select(selectSelectedSpatialFilterFeatureId),
        this.store$.select(selectFilterFeatures),
      ]).pipe(map(([ selectedFeatureId, features ] ) => {
        if (selectedFeatureId) {
          return features.filter(feature => feature.__fid !== selectedFeatureId);
        }
        return features;
      })),
      this.DEFAULT_STYLE,
    ).pipe(takeUntil(this.destroyed)).subscribe();
  }

  public ngOnDestroy(): void {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  public save() {
    this.store$.dispatch(closeForm());
  }

  public cancel() {
    this.store$.dispatch(closeForm());
  }

  public remove(groupId: string) {
    if (!groupId) {
      return;
    }
    this.removeFilterService.removeFilter$(groupId)
      .subscribe((removed) => {
        if (removed) {
          this.store$.dispatch(closeForm());
        }
      });
  }

  private getLabelForReferenceLayer$() {
    return combineLatest([
      this.store$.select(selectReferenceLayer),
      this.dataSourceManagerService.layersWithAttributes$,
    ]).pipe(
      take(1),
      map(([ selectedLayer, referencableLayers ]) => {
        if (!selectedLayer || (referencableLayers || []).length === 0) {
          return null;
        }
        return referencableLayers.find(layer => layer.id === selectedLayer)?.title ?? null;
      }));
  }

}
